import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { checkAdminSetup, setupAdmin, adminLogin, adminLogout, verifyAdminToken } from '../lib/api';

const AdminContext = createContext({
  isAdmin: false,
  isLoading: true,
  isConfigured: false,
  login: () => {},
  logout: () => {},
  setup: () => {},
  checkSetup: () => {},
});

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);
  const [token, setToken] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('quran-player-admin-token') || '';
    }
    return '';
  });

  // Check if admin password is configured
  const checkSetup = useCallback(async () => {
    try {
      const response = await checkAdminSetup();
      setIsConfigured(response.data.is_configured);
      return response.data.is_configured;
    } catch (error) {
      console.error('Failed to check admin setup:', error);
      return false;
    }
  }, []);

  // Verify existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      await checkSetup();
      
      if (token) {
        try {
          const response = await verifyAdminToken(token);
          if (response.data.valid) {
            setIsAdmin(true);
          } else {
            // Token invalid, clear it
            setToken('');
            localStorage.removeItem('quran-player-admin-token');
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Failed to verify token:', error);
          setToken('');
          localStorage.removeItem('quran-player-admin-token');
          setIsAdmin(false);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [token, checkSetup]);

  // Setup admin password for the first time
  const setup = async (password) => {
    try {
      const response = await setupAdmin(password);
      if (response.data.success) {
        const newToken = response.data.token;
        setToken(newToken);
        localStorage.setItem('quran-player-admin-token', newToken);
        setIsAdmin(true);
        setIsConfigured(true);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error('Failed to setup admin:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Erreur lors de la configuration'
      };
    }
  };

  // Login with password
  const login = async (password) => {
    try {
      const response = await adminLogin(password);
      if (response.data.success) {
        const newToken = response.data.token;
        setToken(newToken);
        localStorage.setItem('quran-player-admin-token', newToken);
        setIsAdmin(true);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error('Failed to login:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Erreur de connexion'
      };
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (token) {
        await adminLogout(token);
      }
    } catch (error) {
      console.error('Failed to logout:', error);
    } finally {
      setToken('');
      localStorage.removeItem('quran-player-admin-token');
      setIsAdmin(false);
    }
  };

  return (
    <AdminContext.Provider value={{ 
      isAdmin, 
      isLoading, 
      isConfigured, 
      login, 
      logout, 
      setup,
      checkSetup 
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
